"use client";

import { TrackedLink } from "@/components/analytics/TrackedLink";
import { buildBlockerDiagnosticHref, type BlockerEntrySource } from "@/lib/blockerDiagnosticHref";
import { BRAND_MESSAGING } from "@/lib/messaging";

const { blocker, nextStep, supportPath } = BRAND_MESSAGING.coreIdeas;

type IntentQuizProps = {
  className?: string;
  diagnosticDestination?: string;
  evaluationDestination?: string;
  sourcePage?: BlockerEntrySource;
  sourceSection?: string;
  sourceCta?: string;
  variant?: "home" | "booking" | "offers" | "content" | "footer" | "neutral";
};

const symptomSignals = [
  "Confidence drops as soon as someone is listening",
  "You are not sure what the real issue is",
  "Tension, tightness, or overthinking take over",
  "High notes or consistency still feel unreliable",
];

const copyByVariant = {
  home: {
    eyebrow: "Voice Blocker Quiz",
    heading: "Take the 60-second voice blocker quiz",
    body: `If you are not fully sure what is getting in the way, this short guided quiz gives you a clearer sense of ${blocker} before you choose support.`,
    panelEyebrow: "Inside the quiz",
    bullets: [
      "5 short prompts with one answer each",
      "A blocker summary in Singing Attitude language",
      "A sensible next step based on how your voice currently feels",
    ],
    primaryCta: "Take the quiz now",
    secondaryCta: "Already ready? Book an Evaluation →",
    reassurance: "If you already know you want Liuba to hear the voice directly, the Evaluation remains the clearest first step.",
  },
  booking: {
    eyebrow: "Voice Blocker Quiz",
    heading: "Take the voice blocker quiz first",
    body: `If you want a little more clarity before choosing live support, this short quiz can help you notice ${blocker} and what usually helps first.`,
    panelEyebrow: "Inside the quiz",
    bullets: [
      "Useful if you are hesitating between booking and waiting",
      "Helps you make sense of the friction without pressure",
      `Points you back to ${supportPath}`,
    ],
    primaryCta: "Take the quiz first",
    secondaryCta: "Prefer direct help? Book the Evaluation →",
    reassurance: "Booking stays the primary path on this page. This is simply here for added clarity if you need it.",
  },
  offers: {
    eyebrow: "Voice Blocker Quiz",
    heading: "Take the voice blocker quiz before you choose",
    body: `This short guided quiz helps you understand ${blocker}, so choosing between evaluation, feedback, coaching, or Lab feels simpler.`,
    panelEyebrow: "Inside the quiz",
    bullets: [
      "A clearer sense of the pattern behind the struggle",
      "A calm explanation in Singing Attitude language",
      `A sensible route into ${supportPath}`,
    ],
    primaryCta: "Take the quiz now",
    secondaryCta: "Already know what you want? Continue here →",
    reassurance: "The offers on this page still come first. The guide is here for singers who need help choosing well.",
  },
  content: {
    eyebrow: "Voice Blocker Quiz",
    heading: "If this sounds familiar, take the voice blocker quiz.",
    body:
      "If the pattern in this article feels close to your own experience, this short guided tool can help you make sense of it and choose a sensible next step without overcomplicating the process.",
    panelEyebrow: "Inside the quiz",
    bullets: [
      `Helpful when you recognise the problem but still do not know ${nextStep}`,
      "Gives you a calmer explanation in Singing Attitude language",
      `Points you toward ${supportPath} rather than pushing you into the wrong one`,
    ],
    primaryCta: "Take the quiz",
    secondaryCta: "Prefer direct feedback? Book an Evaluation →",
    reassurance: "This is here as a helpful follow-on to the article, not as something you need to do before continuing.",
  },
  footer: {
    eyebrow: "Voice Blocker Quiz",
    heading: "Take the voice blocker quiz",
    body: `Use this short guided quiz if you want help noticing ${blocker} before choosing support.`,
    panelEyebrow: "Inside the quiz",
    bullets: [
      `Useful when you know something feels off but cannot yet name ${blocker}`,
      "Gives you a clear summary in Singing Attitude language",
      `Points you toward ${supportPath} without pressure`,
    ],
    primaryCta: "Take the quiz",
    secondaryCta: "Prefer direct help? Book an Evaluation →",
    reassurance: "If you already know you want live support, the Evaluation remains the clearest direct step.",
  },
  neutral: {
    eyebrow: "Voice Blocker Quiz",
    heading: "Take the voice blocker quiz",
    body: `Use this short guided quiz to understand ${blocker} and choose the next step that fits without overcomplicating it.`,
    panelEyebrow: "Inside the quiz",
    bullets: [
      `A clearer view of ${blocker}`,
      "A calm explanation in Singing Attitude language",
      "A sensible next step for evaluation, feedback, Lab, or coaching",
    ],
    primaryCta: "Take the quiz",
    secondaryCta: "Already ready? Book an Evaluation →",
    reassurance: "If you already know what support you need, you can ignore this and continue directly.",
  },
} as const;

export function IntentQuiz({
  className,
  diagnosticDestination,
  evaluationDestination = "/evaluation",
  sourcePage = "unknown",
  sourceSection = "diagnostic_teaser",
  sourceCta = "diagnostic_entry",
  variant = "neutral",
}: IntentQuizProps) {
  const relativeDiagnosticHref = buildBlockerDiagnosticHref({
    sourcePage,
    sourceSection,
    sourceCta,
  });
  const diagnosticQueryIndex = relativeDiagnosticHref.indexOf("?");
  const diagnosticHref = diagnosticDestination
    ? `${diagnosticDestination}${diagnosticQueryIndex >= 0 ? relativeDiagnosticHref.slice(diagnosticQueryIndex) : ""}`
    : relativeDiagnosticHref;
  const copy = copyByVariant[variant];
  const isContentVariant = variant === "content";
  const isHomeVariant = variant === "home";

  const sectionStyle = isHomeVariant
    ? {
        background:
          "light-dark(radial-gradient(circle_at_top_left, rgba(212, 193, 138, 0.18), rgba(248, 244, 236, 0.98) 42%), linear-gradient(180deg, rgba(252, 249, 243, 0.98), rgba(245, 239, 229, 0.98)), radial-gradient(circle_at_top_left, rgba(31, 208, 166, 0.14), rgba(10, 15, 27, 0.94) 42%), linear-gradient(180deg, rgba(12, 18, 31, 0.96), rgba(7, 12, 22, 0.98)))",
        borderColor: "light-dark(rgba(214, 199, 173, 0.9), rgba(31, 208, 166, 0.2))",
        boxShadow:
          "light-dark(0 28px 80px rgba(138, 119, 80, 0.18), 0 30px 90px rgba(4, 5, 16, 0.65))",
      }
    : undefined;

  const panelStyle = isHomeVariant || isContentVariant
    ? {
        background: "light-dark(rgba(255, 252, 247, 0.84), rgba(0, 0, 0, 0.2))",
        borderColor: "light-dark(rgba(214, 199, 173, 0.72), rgba(255, 255, 255, 0.1))",
      }
    : undefined;

  return (
    <section
      className={`rounded-[28px] border p-6 sm:p-7${
        isContentVariant
          ? " border-emerald/18 bg-surface/90 shadow-soft dark:bg-surface/80 dark:shadow-[0_18px_56px_rgba(4,5,16,0.45)]"
          : isHomeVariant
            ? " border-emerald/20 shadow-[0_30px_90px_rgba(4,5,16,0.65)]"
            : " border-emerald/20 bg-[radial-gradient(circle_at_top_left,rgba(31,208,166,0.14),rgba(10,15,27,0.94)_42%),linear-gradient(180deg,rgba(12,18,31,0.96),rgba(7,12,22,0.98))] shadow-[0_30px_90px_rgba(4,5,16,0.65)]"
      }${className ? ` ${className}` : ""}`}
      style={sectionStyle}
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(16rem,0.9fr)] lg:items-center">
        <div className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-emerald/80">{copy.eyebrow}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full border border-emerald/25 bg-emerald/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#d9c186]">
                5-question quiz
              </span>
              <span
                className="rounded-full border border-white/10 bg-white/6 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em]"
                style={{
                  color: "light-dark(rgba(67, 74, 84, 0.68), rgba(241,245,249,0.6))",
                  borderColor: isHomeVariant || isContentVariant ? "light-dark(rgba(214, 199, 173, 0.72), rgba(255,255,255,0.1))" : undefined,
                  background: isHomeVariant || isContentVariant ? "light-dark(rgba(255,255,255,0.72), rgba(255,255,255,0.06))" : undefined,
                }}
              >
                About 60 seconds
              </span>
            </div>
            <h3
              className="mt-2 font-serif text-3xl font-semibold tracking-tight"
              style={{ color: "light-dark(rgb(23, 31, 43), rgb(241,245,249))" }}
            >
              {copy.heading}
            </h3>
          </div>
          <p
            className="max-w-2xl text-sm leading-relaxed"
            style={{ color: "light-dark(rgba(55, 65, 81, 0.88), rgba(241,245,249,0.72))" }}
          >
            {copy.body}
          </p>
          <div className="flex flex-wrap gap-2">
            {(isContentVariant ? symptomSignals.slice(0, 3) : symptomSignals).map((signal) => (
              <span
                key={signal}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs uppercase tracking-[0.16em]"
                style={{
                  color: "light-dark(rgba(67, 74, 84, 0.76), rgba(241,245,249,0.6))",
                  borderColor: isHomeVariant || isContentVariant ? "light-dark(rgba(214, 199, 173, 0.68), rgba(255,255,255,0.1))" : undefined,
                  background: isHomeVariant || isContentVariant ? "light-dark(rgba(255,255,255,0.74), rgba(255,255,255,0.05))" : undefined,
                }}
              >
                {signal}
              </span>
            ))}
          </div>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 backdrop-blur-sm" style={panelStyle}>
          <p className="text-xs uppercase tracking-[0.3em] text-emerald/80">{copy.panelEyebrow}</p>
          <ul
            className="mt-4 space-y-3 text-sm"
            style={{ color: "light-dark(rgba(31, 41, 55, 0.82), rgba(241,245,249,0.74))" }}
          >
            {copy.bullets.map((item, index) => (
              <li key={item} className="flex items-start gap-3">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-emerald/25 bg-emerald/10 text-[11px] font-semibold text-[#d9c186]">
                  {index + 1}
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p
            className="mt-4 text-xs leading-relaxed"
            style={{ color: "light-dark(rgba(75, 85, 99, 0.82), rgba(241,245,249,0.52))" }}
          >
            {copy.reassurance}
          </p>
          <div className="mt-5 flex flex-col gap-3">
            <TrackedLink
              href={diagnosticHref}
              prefetch={diagnosticHref.startsWith("/")}
              className="btn-premium inline-flex items-center justify-center rounded-full bg-emerald/90 px-5 py-3 text-sm font-semibold text-midnight"
              eventName="blocker_teaser_primary_click"
              eventProperties={{ source_page: sourcePage, source_section: sourceSection, source_cta: sourceCta }}
            >
              {copy.primaryCta}
            </TrackedLink>
            <TrackedLink
              href={evaluationDestination}
              prefetch={evaluationDestination.startsWith("/")}
              className="text-sm font-semibold text-emerald hover:text-emerald/80"
              eventName="blocker_teaser_secondary_click"
              eventProperties={{ destination: "evaluation" }}
            >
              {copy.secondaryCta}
            </TrackedLink>
          </div>
        </div>
      </div>
    </section>
  );
}
